import React, { useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import Swal from 'sweetalert2'
import { useState } from 'react'
import HomeNav from './HomeNav'
import Footer from './Footer'

const BRAND       = '#6eb446'
const BRAND_SHADOW = '0 8px 24px rgba(110,180,70,0.3)'

const url    = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_URL    : process.env.REACT_APP_URL_DEV
const urlapi = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_URLAPI : process.env.REACT_APP_URLAPI_DEV

const ContactUs: React.FC = () => {
  const { t, i18n }         = useTranslation()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    document.body.classList.add('page-home')
    return () => document.body.classList.remove('page-home')
  }, [])

  const getRecaptchaToken = useCallback(async () => {
    if (!executeRecaptcha) return undefined
    return executeRecaptcha('contact_us')
  }, [executeRecaptcha])

  useEffect(() => { getRecaptchaToken() }, [getRecaptchaToken])

  const formSchema = Yup.object().shape({
    name    : Yup.string().min(3).max(50).required(),
    email   : Yup.string().min(3).max(30).email().required(),
    company : Yup.string().min(3).max(30).required(),
    comments: Yup.string().min(3).max(255).required(),
  })

  const sendContact = async (params: Record<string, string>, { resetForm }: { resetForm: () => void }) => {
    setLoading(true)
    try {
      const token = await getRecaptchaToken()
      const res = await axios.post(`${url}home/contact-us`, { ...params, token_verify: token })
      Swal.fire({ icon: 'success', title: 'Success', text: res.data.response })
      resetForm()
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    backgroundColor: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 6,
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#374151',
  }

  return (
    <>
      <HomeNav />

      <style>{`
        .cu-input:focus { border-color: ${BRAND} !important; box-shadow: 0 0 0 3px rgba(110,180,70,0.12) !important; }
      `}</style>

      {/* Hero */}
      <section style={{ backgroundColor: '#f8faf6', borderBottom: '4px solid #000', padding: '4rem 1.5rem 3rem' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <span style={{
            display: 'inline-block',
            padding: '4px 16px',
            borderRadius: 9999,
            backgroundColor: '#e8f5dc',
            color: BRAND,
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: '1rem',
          }}>
            Label Dictate
          </span>
          <h1 style={{
            margin: 0,
            fontSize: 'clamp(1.8rem, 5vw, 2.75rem)',
            fontWeight: 900,
            color: '#1e293b',
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
          }}>
            {t('contact_us.contact')}
            <span style={{ color: BRAND }}>{t('contact_us.us')}</span>
          </h1>
        </div>
      </section>

      {/* Main */}
      <main style={{ backgroundColor: '#fff', padding: '3rem 1.5rem 5rem' }}>
        <div style={{
          maxWidth: 960,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '3rem',
          alignItems: 'start',
        }}>

          {/* Info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {[0, 1, 2, 3, 4].map(i => (
              <p key={i} style={{
                margin: 0,
                fontSize: '0.92rem',
                color: '#475569',
                lineHeight: 1.8,
                paddingBottom: '1.25rem',
                borderBottom: i < 4 ? '1px solid #f1f5f9' : 'none',
              }}>
                {t(`contact_us.text${i}`)}
              </p>
            ))}
          </div>

          {/* Form */}
          <div style={{
            backgroundColor: '#f8faf6',
            borderRadius: 20,
            padding: '2rem',
            border: '1px solid #e8f5dc',
          }}>
            <h3 style={{ margin: '0 0 1.5rem', fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>
              {t('contact_us.contact')}{t('contact_us.us')}
            </h3>

            <Formik
              initialValues={{ name: '', email: '', company: '', comments: '' }}
              validationSchema={formSchema}
              onSubmit={(values, actions) => sendContact(values, actions as any)}
            >
              <Form noValidate style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                <div>
                  <label htmlFor="name" style={labelStyle}>{t('contact_us.name')}</label>
                  <Field
                    id="name" name="name" type="text"
                    placeholder={t('contact_us.name_1')}
                    className="cu-input"
                    style={inputStyle}
                  />
                  <ErrorMessage name="name" render={msg => (
                    <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                  )} />
                </div>

                <div>
                  <label htmlFor="email" style={labelStyle}>{t('contact_us.email')}</label>
                  <Field
                    id="email" name="email" type="email"
                    placeholder={t('contact_us.email')}
                    className="cu-input"
                    style={inputStyle}
                  />
                  <ErrorMessage name="email" render={msg => (
                    <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                  )} />
                </div>

                <div>
                  <label htmlFor="company" style={labelStyle}>{t('contact_us.company')}</label>
                  <Field
                    id="company" name="company" type="text"
                    placeholder={t('contact_us.company_1')}
                    className="cu-input"
                    style={inputStyle}
                  />
                  <ErrorMessage name="company" render={msg => (
                    <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                  )} />
                </div>

                <div>
                  <label htmlFor="comments" style={labelStyle}>{t('contact_us.comments')}</label>
                  <Field
                    as="textarea" id="comments" name="comments" rows={4}
                    className="cu-input"
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 100 }}
                  />
                  <ErrorMessage name="comments" render={msg => (
                    <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                  )} />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: 9999,
                    border: 'none',
                    backgroundColor: loading ? '#a3d08a' : BRAND,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: BRAND_SHADOW,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    transition: 'filter 0.15s',
                    marginTop: 4,
                  }}
                  onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = 'brightness(1.08)' }}
                  onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)' }}
                >
                  {loading && (
                    <span style={{
                      width: 16, height: 16, borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff',
                      display: 'inline-block',
                      animation: 'spin 0.7s linear infinite',
                    }} />
                  )}
                  {t('contact_us.send_form')}
                </button>

                <p style={{ margin: 0, fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center', lineHeight: 1.5 }}>
                  {t('footer.recaptcha')}{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#64748b' }}>
                    {t('footer.privacy_google')}
                  </a>
                  {' '}&{' '}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#64748b' }}>
                    {t('footer.terms_google')}
                  </a>
                  {i18n.language === 'en' ? ' apply.' : ' de Google.'}
                </p>

              </Form>
            </Formik>
          </div>
        </div>
      </main>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Footer />
    </>
  )
}

export default ContactUs
